import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, CalendarPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ withCredentials: true });

const fmtDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
};

export default function QuickBook({ date, time, barber, onClose, onDone }) {
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("agendar");
  const [duration, setDuration] = useState(60);
  const [reason, setReason] = useState("");

  useEffect(() => {
    axios.get(`${API}/services`)
      .then((r) => setServices(r.data))
      .catch(() => toast.error("Erro ao carregar serviços."));
  }, []);

  const submit = async () => {
    if (mode === "bloquear") {
      setSaving(true);
      try {
        await http.post(`${API}/admin/blocks`, { barber_id: barber.id, date, time, duration, reason: reason.trim() });
        toast.success("Horário bloqueado na grade.");
        onDone();
        onClose();
      } catch (e) {
        const d = e.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Erro ao bloquear.");
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!serviceId) {
      toast.error("Escolha o serviço.");
      return;
    }
    if (name.trim().length < 2) {
      toast.error("Informe o nome do cliente.");
      return;
    }
    setSaving(true);
    try {
      await http.post(`${API}/admin/appointments`, {
        service_id: serviceId,
        barber_id: barber.id,
        date,
        time,
        name: name.trim(),
        whatsapp,
      });
      toast.success("Agendado na grade!");
      onDone();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao agendar.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "focus-gold w-full bg-ink border border-white/15 text-cream font-manrope px-4 py-3 text-sm outline-none placeholder:text-smoke/50";

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="quickbook-modal">
      <div className="w-full max-w-md border border-gold/40 bg-surface p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-bebas text-3xl tracking-wide text-cream flex items-center gap-2">
              <CalendarPlus className="w-6 h-6 text-gold" /> Agendar horário
            </h3>
            <p className="text-sm text-gold mt-1 capitalize" data-testid="quickbook-slot">
              {barber.name} · {time} · {fmtDate(date)}
            </p>
          </div>
          <button onClick={onClose} aria-label="Fechar" data-testid="quickbook-close" className="focus-gold p-2 border border-white/15 text-smoke hover:border-gold hover:text-gold transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2" data-testid="quickbook-mode">
          <button
            type="button"
            onClick={() => setMode("agendar")}
            data-testid="quickbook-mode-agendar"
            className={`focus-gold font-bebas tracking-widest py-2.5 border transition-colors ${mode === "agendar" ? "bg-gold text-ink border-gold" : "border-white/15 text-smoke hover:border-gold hover:text-gold"}`}
          >
            Agendar cliente
          </button>
          <button
            type="button"
            onClick={() => setMode("bloquear")}
            data-testid="quickbook-mode-bloquear"
            className={`focus-gold font-bebas tracking-widest py-2.5 border transition-colors ${mode === "bloquear" ? "bg-red-600 text-cream border-red-600" : "border-white/15 text-smoke hover:border-red-500 hover:text-red-400"}`}
          >
            Bloquear horário
          </button>
        </div>

        {mode === "agendar" ? (
        <div className="mt-6 space-y-4">
          <div>
            <label htmlFor="quickbook-service" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">Serviço</label>
            <select
              id="quickbook-service"
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              data-testid="quickbook-service"
              className={`${inputCls} [color-scheme:dark]`}
            >
              <option value="">Escolha o serviço...</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>{s.name} · R$ {s.price}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="quickbook-name" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">Nome do cliente</label>
            <input
              id="quickbook-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: João"
              autoFocus
              data-testid="quickbook-name"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="quickbook-whatsapp" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">WhatsApp (opcional)</label>
            <input
              id="quickbook-whatsapp"
              type="tel"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="(47) 99999-9999"
              data-testid="quickbook-whatsapp"
              className={inputCls}
            />
          </div>
        </div>
        ) : (
        <div className="mt-6 space-y-4">
          <div>
            <label htmlFor="quickbook-duration" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">Duração do bloqueio</label>
            <select
              id="quickbook-duration"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              data-testid="quickbook-duration"
              className={`${inputCls} [color-scheme:dark]`}
            >
              <option value={30}>30 minutos</option>
              <option value={60}>1 hora</option>
              <option value={90}>1h30</option>
              <option value={120}>2 horas</option>
            </select>
          </div>
          <div>
            <label htmlFor="quickbook-reason" className="text-xs uppercase tracking-[0.25em] text-smoke block mb-2">Motivo (opcional)</label>
            <input
              id="quickbook-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: Almoço, folga, imprevisto"
              maxLength={60}
              data-testid="quickbook-reason"
              className={inputCls}
            />
          </div>
        </div>
        )}

        <button
          onClick={submit}
          disabled={saving}
          data-testid="quickbook-submit"
          className={`focus-gold mt-7 w-full font-bebas text-xl tracking-widest py-3 transition-colors disabled:opacity-40 ${mode === "bloquear" ? "bg-red-600 text-cream hover:bg-red-700" : "bg-gold text-ink hover:bg-golddark"}`}
        >
          {saving ? "Salvando..." : mode === "bloquear" ? "Bloquear na grade" : "Confirmar na grade"}
        </button>
      </div>
    </div>
  );
}
